<template>
  <div
    class="viewer-part-search"
    data-unit="viewer-part-search"
  >
    <div class="field has-addons">
      <div class="control is-expanded has-icons-left has-icons-right">
        <input
          v-model="query"
          class="input is-primary"
          data-unit="viewer-part-search-input"
          type="search"
          autocomplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="viewer-part-search-results"
          :aria-activedescendant="selectedIndex >= 0 ? `viewer-part-search-result-${selectedIndex}` : undefined"
          :aria-expanded="query.length > 0 && results.length > 0"
          :aria-label="$t('searchInContent', { contentName: snapshotName })"
          :disabled="!available"
          :placeholder="$t('searchInContent', { contentName: snapshotName })"
          @input="handleQueryChange()"
          @keydown.down.prevent="selectNext"
          @keydown.up.prevent="selectPrevious"
        >
        <span
          class="icon is-small is-left fas fa-search"
          aria-hidden="true"
        />
        <button
          v-if="query"
          class="button is-white clear-search"
          data-unit="clear-viewer-part-search"
          type="button"
          :aria-label="$t('clearSearch')"
          @click="clear"
        >
          <span
            class="icon is-small fas fa-times"
            aria-hidden="true"
          />
        </button>
      </div>
      <span
        v-if="query"
        class="button is-static result-count"
        data-unit="viewer-part-search-count"
        role="status"
        aria-live="polite"
      >
        {{ results.length }}
      </span>
      <button
        v-if="canNavigate"
        class="button"
        data-unit="previous-viewer-part-result"
        type="button"
        :aria-label="$t('threeD.previousResult')"
        @click="selectPrevious"
      >
        <span
          class="icon is-small fas fa-chevron-left"
          aria-hidden="true"
        />
      </button>
      <button
        v-if="canNavigate"
        class="button"
        data-unit="next-viewer-part-result"
        type="button"
        :aria-label="$t('threeD.nextResult')"
        @click="selectNext"
      >
        <span
          class="icon is-small fas fa-chevron-right"
          aria-hidden="true"
        />
      </button>
    </div>

    <p
      v-if="!available"
      class="help"
      data-unit="viewer-part-search-unavailable"
      role="status"
    >
      {{ $t('threeD.partInformationUnavailableHelp') }}
    </p>
    <p
      v-else-if="query && results.length === 0"
      class="viewer-part-search-empty"
      data-unit="viewer-part-search-empty"
      role="status"
    >
      {{ $t('noSearchResultsHelpText') }}
    </p>
    <ul
      v-else-if="query && results.length > 0"
      id="viewer-part-search-results"
      class="viewer-part-results menu-list"
      data-unit="viewer-part-search-results"
      role="listbox"
    >
      <li
        v-for="(result, index) in results"
        :key="result.key"
      >
        <button
          :id="`viewer-part-search-result-${index}`"
          class="button is-white viewer-part-result"
          :class="{ 'is-active': index === selectedIndex }"
          type="button"
          role="option"
          :aria-selected="index === selectedIndex"
          @click="selectResult(index)"
        >
          <span
            v-if="result.part.visible !== false &&
              result.part.partNumberVisible !== false &&
              result.part.partNumber"
            class="part-number"
          >
            {{ result.part.partNumber }}
          </span>
          <span class="part-name">{{ result.part.name }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { filterThreeDViewerParts } from '@/helpers/threeDViewerPartSearch'
import type {
  ThreeDViewerPartSearchResult,
  ThreeDViewerPartSearchSource
} from '@/interfaces/library'

const props = withDefaults(defineProps<{
  available?: boolean
  snapshotName: string
  source?: ThreeDViewerPartSearchSource[]
}>(), {
  source: () => []
})

const emit = defineEmits<{
  clearFocus: []
  focus: [result: ThreeDViewerPartSearchResult]
}>()

const query = ref('')
const selectedIndex = ref(-1)
const selectedResultKey = ref<string | null>(null)
const results = computed(() => filterThreeDViewerParts(props.source, query.value))
const canNavigate = computed(() => results.value.length > 1)

function handleQueryChange (forceFocus = false) {
  const singleResult = results.value.length === 1 ? results.value[0] : null
  if (!forceFocus && singleResult?.key === selectedResultKey.value) {
    selectedIndex.value = 0
    return
  }

  clearResultSelection()
  emit('clearFocus')
  if (singleResult) selectResult(0)
}

function selectResult (index: number) {
  const result = results.value[index]
  if (!result) return

  selectedIndex.value = index
  selectedResultKey.value = result.key
  emit('focus', result)
}

function selectNext () {
  if (!canNavigate.value) return
  selectResult(selectedIndex.value < 0 ? 0 : (selectedIndex.value + 1) % results.value.length)
}

function selectPrevious () {
  if (!canNavigate.value) return
  selectResult(selectedIndex.value < 0
    ? results.value.length - 1
    : (selectedIndex.value - 1 + results.value.length) % results.value.length
  )
}

function clear () {
  query.value = ''
  clearResultSelection()
  emit('clearFocus')
}

function clearResultSelection () {
  selectedIndex.value = -1
  selectedResultKey.value = null
}

function setQuery (value: string) {
  query.value = value
  handleQueryChange()
}

watch(() => props.source, () => {
  if (query.value) handleQueryChange(true)
})

defineExpose({ clear, clearResultSelection, setQuery })
</script>

<style scoped>
.viewer-part-search {
  left: 50%;
  max-width: calc(100% - 2rem);
  position: absolute;
  top: 0.75rem;
  transform: translateX(-50%);
  width: 28rem;
  z-index: 5;
}
.clear-search {
  border: 0;
  height: calc(100% - 2px);
  position: absolute;
  right: 1px;
  top: 1px;
}
.result-count {
  min-width: 2.5rem;
}
.viewer-part-results,
.viewer-part-search-empty {
  background: white;
  border: 1px solid #dbdbdb;
  box-shadow: 0 0.25rem 0.5rem rgba(10, 10, 10, 0.1);
  max-height: 14rem;
  overflow-y: auto;
}
.viewer-part-search-empty {
  padding: 0.75rem;
}
.viewer-part-result {
  display: flex;
  height: auto;
  justify-content: flex-start;
  width: 100%;
}
.part-number {
  flex: 0 0 auto;
  margin-right: 0.75rem;
}
.part-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media screen and (max-width: 768px) {
  .viewer-part-search {
    max-width: calc(100% - 1rem);
    top: 0.5rem;
  }
}
</style>
